import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ForceGraph2D from "react-force-graph-2d"
import { buildAdjacency, buildGraphData, neighborsForNode, sortNodesForRender } from "./graphWidgetData.js"
import GraphFilterPanel from "./GraphFilterPanel.jsx"
import { applyGraphFilters, DEFAULT_CONFIDENCE_THRESHOLD } from "./graphWidgetFilters.js"
import GraphNodeLegend from "./GraphNodeLegend.jsx"
import { drawGraphNode, shapeForNodeType } from "./graphWidgetNodeCanvas.js"
import {
  applyOpacity,
  edgeStyleForLink,
  nodeColorForType,
  readGraphTheme,
} from "./graphWidgetTheme.js"
import { THEME_CHANGE_EVENT } from "../theme.js"
import { useDebouncedValue } from "./useDebouncedValue.js"
import {
  ALL_EDGE_GROUPS,
  DEFAULT_DEPTH,
  parseGraphUrlState,
  syncGraphUrlState,
} from "./graphWidgetUrlState.js"

const MIN_DEPTH = 1
const MAX_DEPTH = 4
const DEBOUNCE_MS = 300

function readMountConfig(mountElement) {
  const personalGraphId = mountElement.dataset.personalGraphId
  if (personalGraphId) {
    return { mode: "personal", personalGraphId }
  }

  const nodeType = mountElement.dataset.nodeType
  const nodeId = mountElement.dataset.nodeId
  const defaultCenterShorthand = mountElement.dataset.defaultCenterShorthand ?? null

  if (!nodeType || !nodeId) {
    return null
  }

  const urlState = parseGraphUrlState(new URLSearchParams(window.location.search), defaultCenterShorthand)

  return { mode: "hop", nodeType, nodeId, defaultCenterShorthand, urlState }
}

async function fetchPersonalSubgraph(personalGraphId) {
  const response = await fetch(
    `/graph/personal_graphs/${encodeURIComponent(personalGraphId)}.json`,
    {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    }
  )

  if (response.status === 401 || response.status === 403) {
    throw new Error("Sign in to view this personal graph.")
  }

  if (response.status === 429) {
    const retryAfter = Number.parseInt(response.headers.get("Retry-After") ?? "60", 10)
    const error = new Error("Too many graph requests. Please wait and try again.")
    error.status = 429
    error.retryAfterSeconds = Number.isFinite(retryAfter) ? retryAfter : 60
    throw error
  }

  if (!response.ok) {
    throw new Error(`Graph request failed (${response.status})`)
  }

  return response.json()
}

async function fetchSubgraph({ nodeType, nodeId }, depth) {
  const response = await fetch(
    `/graph/${encodeURIComponent(nodeType)}/${encodeURIComponent(nodeId)}.json?depth=${depth}`,
    {
      headers: { Accept: "application/json" },
    }
  )

  if (response.status === 429) {
    const retryAfter = Number.parseInt(response.headers.get("Retry-After") ?? "60", 10)
    const error = new Error("Too many graph requests. Please wait and try again.")
    error.status = 429
    error.retryAfterSeconds = Number.isFinite(retryAfter) ? retryAfter : 60
    throw error
  }

  if (!response.ok) {
    throw new Error(`Graph request failed (${response.status})`)
  }

  return response.json()
}

function decorateGraphData(graphData, theme, centerId) {
  const nodes = graphData.nodes.map((node) => {
    const isCenter = node.id === centerId
    return {
      ...node,
      isCenter,
      color: isCenter ? theme.center : nodeColorForType(node.node_type, theme),
      shape: shapeForNodeType(node.node_type),
      val: isCenter ? 8 : 4,
    }
  })

  return {
    nodes: sortNodesForRender(nodes, centerId),
    links: graphData.links,
  }
}

export default function GraphWidget({ mountElement }) {
  const [config] = useState(() => readMountConfig(mountElement))
  const isPersonalMode = config?.mode === "personal"
  const [depth, setDepth] = useState(() => (isPersonalMode ? 0 : config?.urlState?.depth ?? DEFAULT_DEPTH))
  const [edgeGroups, setEdgeGroups] = useState(() =>
    isPersonalMode ? [...ALL_EDGE_GROUPS] : config?.urlState?.edgeGroups ?? [...ALL_EDGE_GROUPS]
  )
  const [confidenceThreshold, setConfidenceThreshold] = useState(DEFAULT_CONFIDENCE_THRESHOLD)
  const [verifiedOnly, setVerifiedOnly] = useState(false)
  const [centerShorthand, setCenterShorthand] = useState(
    () => config?.urlState?.centerShorthand ?? config?.defaultCenterShorthand ?? null
  )
  const debouncedDepth = useDebouncedValue(depth, DEBOUNCE_MS)
  const [status, setStatus] = useState(config ? "loading" : "idle")
  const [payload, setPayload] = useState(null)
  const [error, setError] = useState(null)
  const [rateLimitedUntil, setRateLimitedUntil] = useState(null)
  const [focusedNodeId, setFocusedNodeId] = useState(null)
  const [dimensions, setDimensions] = useState({ width: 640, height: 420 })

  const [theme, setTheme] = useState(() => readGraphTheme())
  const graphRef = useRef(null)
  const widgetRef = useRef(null)
  const sliderRef = useRef(null)
  const liveRegionRef = useRef(null)
  const graphContainerRef = useRef(null)
  const focusSessionRef = useRef({ nodeId: null, neighborIndex: -1 })

  const filteredPayload = useMemo(() => {
    if (!payload) return null

    return applyGraphFilters(payload, {
      edgeGroups,
      confidenceThreshold,
      verifiedOnly,
      keepAllNodes: isPersonalMode,
    })
  }, [payload, edgeGroups, confidenceThreshold, verifiedOnly, isPersonalMode])

  const graphData = useMemo(() => {
    if (!filteredPayload) return { nodes: [], links: [] }
    const raw = buildGraphData(filteredPayload)
    return decorateGraphData(raw, theme, payload?.center?.id)
  }, [filteredPayload, payload, theme])

  const adjacency = useMemo(() => buildAdjacency(graphData.links), [graphData.links])

  useEffect(() => {
    const refreshTheme = () => setTheme(readGraphTheme())
    window.addEventListener(THEME_CHANGE_EVENT, refreshTheme)
    return () => window.removeEventListener(THEME_CHANGE_EVENT, refreshTheme)
  }, [])

  const announceCounts = useCallback((nodeCount, edgeCount) => {
    if (!liveRegionRef.current) return
    liveRegionRef.current.textContent = `Showing ${nodeCount} nodes and ${edgeCount} connections.`
  }, [])

  useEffect(() => {
    if (!config) return

    let cancelled = false

    setStatus("loading")
    setError(null)

    const request = isPersonalMode
      ? fetchPersonalSubgraph(config.personalGraphId)
      : fetchSubgraph(config, debouncedDepth)

    request
      .then((data) => {
        if (cancelled) return
        setPayload(data)
        setStatus("ready")
        setFocusedNodeId(data.center?.id ?? null)
        if (config.defaultCenterShorthand) {
          setCenterShorthand(config.defaultCenterShorthand)
        }
      })
      .catch((fetchError) => {
        if (cancelled) return
        if (fetchError.status === 429) {
          setRateLimitedUntil(Date.now() + fetchError.retryAfterSeconds * 1000)
        }
        setError(fetchError.message)
        setStatus("error")
      })

    return () => {
      cancelled = true
    }
  }, [config, debouncedDepth, isPersonalMode])

  useEffect(() => {
    if (isPersonalMode || !config?.defaultCenterShorthand) return

    syncGraphUrlState({
      depth: debouncedDepth,
      edgeGroups,
      centerShorthand,
      defaultCenterShorthand: config.defaultCenterShorthand,
    })
  }, [config, debouncedDepth, edgeGroups, centerShorthand, isPersonalMode])

  useEffect(() => {
    if (status !== "ready" || !filteredPayload) return
    if (focusedNodeId != null && !graphData.nodes.some((node) => node.id === focusedNodeId)) {
      setFocusedNodeId(payload?.center?.id ?? null)
      focusSessionRef.current = { nodeId: null, neighborIndex: -1 }
    }
  }, [status, filteredPayload, graphData.nodes, focusedNodeId, payload])

  useEffect(() => {
    if (status !== "ready" || !payload) return
    announceCounts(graphData.nodes.length, graphData.links.length)
  }, [status, payload, graphData.nodes.length, graphData.links.length, announceCounts])

  useEffect(() => {
    if (!rateLimitedUntil) return

    const remainingMs = rateLimitedUntil - Date.now()
    if (remainingMs <= 0) {
      setRateLimitedUntil(null)
      return
    }

    const timer = window.setTimeout(() => setRateLimitedUntil(null), remainingMs)
    return () => window.clearTimeout(timer)
  }, [rateLimitedUntil])

  useEffect(() => {
    const element = graphContainerRef.current
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      setDimensions({
        width: Math.max(entry.contentRect.width, 320),
        height: Math.max(entry.contentRect.height, 320),
      })
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [status])

  const navigateToNode = useCallback((node) => {
    if (node?.url) {
      window.location.assign(node.url)
    }
  }, [])

  const moveFocus = useCallback(
    (direction) => {
      if (focusedNodeId == null) return

      if (focusSessionRef.current.nodeId !== focusedNodeId) {
        focusSessionRef.current = { nodeId: focusedNodeId, neighborIndex: -1 }
      }

      const neighbors = neighborsForNode(focusedNodeId, adjacency)
      if (!neighbors.length) return

      const step = direction === "next" ? 1 : -1
      const nextIndex =
        (focusSessionRef.current.neighborIndex + step + neighbors.length) % neighbors.length
      focusSessionRef.current.neighborIndex = nextIndex

      const targetId = neighbors[nextIndex]
      setFocusedNodeId(targetId)

      const targetNode = graphData.nodes.find((node) => node.id === targetId)
      if (targetNode?.x != null && targetNode?.y != null) {
        graphRef.current?.centerAt(targetNode.x, targetNode.y, 400)
      }
    },
    [focusedNodeId, adjacency, graphData.nodes]
  )

  const handleWidgetKeyDown = useCallback(
    (event) => {
      if (event.target === sliderRef.current) return

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault()
          moveFocus("next")
          break
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault()
          moveFocus("prev")
          break
        case "Enter":
          event.preventDefault()
          navigateToNode(graphData.nodes.find((node) => node.id === focusedNodeId))
          break
        case "Escape":
          event.preventDefault()
          sliderRef.current?.focus()
          break
        default:
          break
      }
    },
    [moveFocus, navigateToNode, graphData.nodes, focusedNodeId]
  )

  const handleNodeClick = useCallback(
    (node) => {
      setFocusedNodeId(node.id)
      navigateToNode(node)
    },
    [navigateToNode]
  )

  const sliderDisabled = !isPersonalMode && (status === "loading" || rateLimitedUntil != null)

  if (!config) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface-muted p-6 text-center text-sm text-text-secondary">
        <p className="font-medium text-text">Citation graph</p>
        <p className="mt-1">No graph data is available for this page yet.</p>
      </div>
    )
  }

  const centerLabel = payload?.center?.label ?? "this node"
  const nodeCount = graphData.nodes.length
  const edgeCount = graphData.links.length

  return (
    <div className="rounded-lg border border-border bg-surface-elevated p-4 text-sm text-text-secondary sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-sans text-lg font-semibold text-text">Citation graph</h2>
          <p className="mt-1 text-text-secondary">
            {isPersonalMode ? (
              <>
                Center: <span className="font-medium text-text">{centerLabel}</span>.
                Purple dashed links group your selections; solid links are citation edges in the corpus.
              </>
            ) : (
              <>
                Centered on <span className="font-medium text-text">{centerLabel}</span>.
              </>
            )}
          </p>
        </div>
        {!isPersonalMode ? (
          <label className="flex min-w-[12rem] flex-col gap-1 text-xs font-medium text-text-secondary">
            <span>Graph depth</span>
            <input
              ref={sliderRef}
              type="range"
              min={MIN_DEPTH}
              max={MAX_DEPTH}
              step={1}
              value={depth}
              disabled={sliderDisabled}
              onChange={(event) => setDepth(Number.parseInt(event.target.value, 10))}
              aria-label={`Graph depth: ${depth} hops`}
              aria-valuemin={MIN_DEPTH}
              aria-valuemax={MAX_DEPTH}
              aria-valuenow={depth}
              className="w-full accent-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-text-muted">{depth} hop{depth === 1 ? "" : "s"}</span>
          </label>
        ) : null}
      </div>

      <GraphFilterPanel
        edgeGroups={edgeGroups}
        onEdgeGroupsChange={setEdgeGroups}
        confidenceThreshold={confidenceThreshold}
        onConfidenceThresholdChange={setConfidenceThreshold}
        verifiedOnly={verifiedOnly}
        onVerifiedOnlyChange={setVerifiedOnly}
        disabled={status === "loading"}
      />

      <p ref={liveRegionRef} className="sr-only" aria-live="polite" />

      {rateLimitedUntil ? (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-950 dark:text-amber-100" role="status">
          Too many requests. The depth slider is paused briefly. Try again in a minute.
        </p>
      ) : null}

      {status === "loading" ? (
        <div
          className="mt-4 rounded-md border border-border bg-surface-muted p-8 text-center text-text-secondary"
          aria-busy="true"
        >
          Loading citation graph…
        </div>
      ) : null}

      {status === "error" ? (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-100" role="alert">
          Could not load the citation graph. {error}
        </div>
      ) : null}

      {status === "ready" ? (
        <>
          {payload?.truncated ? (
            <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              Showing {nodeCount} of {nodeCount + (payload.omitted_node_count ?? 0)} connected nodes.
              Reduce depth or apply filters to see more.
            </p>
          ) : null}

          <div
            ref={widgetRef}
            tabIndex={0}
            role="application"
            aria-label="Interactive citation graph. Use arrow keys to move between connected nodes and press Enter to open the selected node."
            onKeyDown={handleWidgetKeyDown}
            className="mt-4 rounded-md border border-border bg-surface-muted outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
          >
            <div ref={graphContainerRef} className="h-[min(60vh,28rem)] w-full">
              <ForceGraph2D
                ref={graphRef}
                width={dimensions.width}
                height={dimensions.height}
                graphData={graphData}
                backgroundColor={theme.surfaceMuted || "#f8fafc"}
                nodeLabel={(node) =>
                  `${node.isCenter ? "Center · " : ""}${node.name}\n${node.node_type.replaceAll("_", " ")}\n${
                    node.connectionCount
                  } connection${node.connectionCount === 1 ? "" : "s"}`
                }
                linkLabel={(link) =>
                  link.link_kind === "personal"
                    ? "Your selection · grouped under center node"
                    : `${link.edge_type.replaceAll("_", " ")} · confidence ${link.confidence}${
                        link.verified ? " · verified" : ""
                      }${link.excerpt ? `\n${link.excerpt}` : ""}`
                }
                nodeCanvasObject={(node, ctx, globalScale) =>
                  drawGraphNode(node, ctx, globalScale, {
                    theme,
                    focused: node.id === focusedNodeId,
                  })
                }
                nodeCanvasObjectMode={() => "replace"}
                linkColor={(link) => {
                  const style = edgeStyleForLink(link, theme)
                  return applyOpacity(style.color, style.opacity)
                }}
                linkWidth={(link) => edgeStyleForLink(link, theme).width}
                linkLineDash={(link) => edgeStyleForLink(link, theme).dash}
                onNodeClick={handleNodeClick}
                cooldownTicks={80}
                d3AlphaDecay={0.03}
                d3VelocityDecay={0.35}
              />
            </div>
          </div>

          <GraphNodeLegend theme={theme} />

          <p className="mt-3 text-xs text-text-muted" aria-hidden="true">
            {nodeCount} nodes · {edgeCount} edges · Tab into the graph, use arrow keys to explore, Enter to open a
            node.
          </p>
        </>
      ) : null}
    </div>
  )
}
